import { useFilesContext } from '@/contexts/filesContext.tsx'
import { useEffect, useRef, useState } from 'react'
import DriveHeader from '@/components/DriveHeader.tsx'
import { FileInput, Loading, Modal, Textarea, Tooltip } from 'react-daisyui'
import { Button } from '@/components/Button.tsx'
import { Link } from 'react-router-dom'
import { IconArrowBack, IconCamera, IconLock, IconMicrophone, IconPencil, IconTrash, IconX } from '@tabler/icons-react'
import AlbumGuestbookEntry from '@/models/albumGuestbookEntry.ts'
import { updateHelper } from '@/controller/updateHelper.ts'
import { useClient } from '@/stores/useClient.ts'
import { Album } from '@/models/album.ts'
import { isStatusFinalState, useUploadQueue } from '@/stores/useUploadQueue.ts'
import { GuestbookEntryProvider, useGuestbookEntryContext } from '@/contexts/guestbookEntryContext.tsx'
import { useProfile } from '@/stores/useAuth.ts'
import CreateProfileModal from '@/components/CreateProfileModal.tsx'
import { useInView } from 'react-intersection-observer'
import * as lamejs from '@breezystack/lamejs';
import { useDrives } from '@/stores/useDrives.ts'
import { useTranslation } from 'react-i18next'

export default function GuestbookPage() {
  const { currentAlbum } = useFilesContext()!
  const album = currentAlbum!
  const { currentDrive } = useDrives()

  return <GuestbookEntryProvider drive={currentDrive!} albumId={album.id}>
    <GuestbookPageContent album={album} />
  </GuestbookEntryProvider>
}

function GuestbookPageContent({ album }: { album: Album }) {
  const { isLoading, guestbookEntries, removeGuestbookEntry, hasNextPage, fetchNextPage } = useGuestbookEntryContext()!
  const { ref, inView } = useInView()
  const [isCreating, setIsCreating] = useState(false)
  const profileCreateRef = useRef<HTMLDialogElement>(null);
  const { profile } = useProfile()
  const { t } = useTranslation()

  useEffect(() => {
    if (hasNextPage && fetchNextPage && inView) {
      fetchNextPage()
    }
  }, [inView, fetchNextPage, hasNextPage])

  function onCreateFinish() {
    setIsCreating(false)
  }

  function onGuestbookEntryDelete(guestbookEntry: AlbumGuestbookEntry) {
    removeGuestbookEntry(guestbookEntry)
  }

  function onRegister() {
    setIsCreating(true)
  }

  function onCreateClick() {
    if (profile) {
      setIsCreating(true)
    } else {
      profileCreateRef.current!.showModal()
    }
  }

  return <div>
    <DriveHeader />
    <div className="container mx-auto flex flex-col gap-3 justify-center">
      <div className='flex flex-row gap-3 px-2 justify-center'>
        <Link to='..'>
          <Button size='sm' color='primary' variant='outline'><IconArrowBack /> {t('common.backToAlbum')}</Button>
        </Link>
      </div>
      <div className='container px-4 mx-auto flex flex-col gap-3 items-center max-w-[30rem]'>
        <h2 className='font-serif text-3xl mb-3'>{t('common.guestbook')}</h2>

        { album?.is_guestbook_private && <div className='container mx-auto px-4 flex justify-center mb-3 pt-1'>
          <Tooltip message={t('common.guestsCanOnlyUpload')} color='secondary'>
          <span className='text-sm opacity-50 flex flex-row gap-1 items-center cursor-pointer mt-[-1rem]'>
            <IconLock size={20} /> {t('common.privateGuestbook')}
          </span>
          </Tooltip>
        </div> }

        { !album.is_guestbook_upload_disabled && !isCreating && <Button color='primary' onClick={onCreateClick}>
          <IconPencil /> {t('common.newGuestbookEntry')}
        </Button> }
        { isCreating && <Button color='primary' onClick={() => setIsCreating(false)}>
          <IconX /> {t('common.cancel')}
        </Button> }
        { isCreating && <CreateEntry album={album} onCreateFinish={onCreateFinish} /> }
        <div className='flex flex-col gap-5 w-full'>
          { isLoading && <div className='mt-5 w-full flex justify-center items-center'>
            <Loading color='primary' />
          </div>}
          { !isLoading && (!guestbookEntries || guestbookEntries.length === 0) && <div className='mt-5 w-full flex justify-center items-center'>
            <p className='mt-5'>
              {t('games.beFirst')}
            </p>
          </div> }
          { guestbookEntries && guestbookEntries.map(entry => <AlbumGuestbookEntryComponent key={entry.id} album={album} entry={entry} onDelete={onGuestbookEntryDelete} />)}
          <div className='w-1 h-1 mb-5' ref={ref} />
        </div>
      </div>
    </div>
    <CreateProfileModal album={album!} innerRef={profileCreateRef} onRegister={onRegister} />
  </div>
}

// A simple helper to format seconds into MM:SS format
const formatTime = (timeInSeconds: number) => {
  const minutes = Math.floor(timeInSeconds / 60).toString().padStart(2, '0');
  const seconds = (timeInSeconds % 60).toString().padStart(2, '0');
  return `${minutes}:${seconds}`;
};

export function CreateEntry({ album, onCreateFinish }: { album: Album, onCreateFinish: () => void }) {
  const { refetch } = useGuestbookEntryContext()!
  const [content, setContent] = useState('')
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null)
  const [audioURL, setAudioURL] = useState<string | null>(null)

  const [recordingState, setRecordingState] = useState<'idle' | 'initiating' | 'recording' | 'processing'>('idle')
  const [elapsedTime, setElapsedTime] = useState(0)
  const timerIntervalRef = useRef<any | null>(null)

  const inputFileRef = useRef<HTMLInputElement>(null);
  const [imageFile, setImageFile] = useState<File | null>(null)
  const [isPosting, setIsPosting] = useState(false)
  const { createGuestbookEntry } = useClient()
  const { registerFileUploads, uploadQueue } = useUploadQueue()
  const [createdGuestbookEntry, setCreatedGuestbookEntry] = useState<AlbumGuestbookEntry | null>(null)

  const audioContextRef = useRef<AudioContext | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const scriptProcessorRef = useRef<ScriptProcessorNode | null>(null)
  const rawAudioChunksRef = useRef<Float32Array[]>([])
  const { t } = useTranslation()

  useEffect(() => {
    return () => {
      if (timerIntervalRef.current) clearInterval(timerIntervalRef.current)
    };
  }, [])

  const onAudioClick = async () => {
    if (recordingState === 'recording') {
      stopAudio()
    } else if (recordingState === 'idle') {
      await recordAudio()
    }
  };

  const recordAudio = async () => {
    setRecordingState('initiating')
    rawAudioChunksRef.current = []
    setAudioURL('')
    setAudioBlob(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      // @ts-ignore
      const context = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = context;

      const scriptProcessor = context.createScriptProcessor(4096, 1, 1);
      scriptProcessorRef.current = scriptProcessor;

      scriptProcessor.onaudioprocess = (event) => {
        if (rawAudioChunksRef.current.length === 0) {
          setRecordingState('recording');
          timerIntervalRef.current = setInterval(() => setElapsedTime(prev => prev + 1), 1000);
        }
        const rawData = event.inputBuffer.getChannelData(0);
        rawAudioChunksRef.current.push(new Float32Array(rawData));
      };

      const source = context.createMediaStreamSource(stream);
      source.connect(scriptProcessor);
      scriptProcessor.connect(context.destination);

    } catch (error) {
      console.error("Error starting recording:", error);
      alert("Could not start recording. Please grant microphone permissions and try again.");
      setRecordingState('idle');
    }
  };

  const stopAudio = () => {
    // 1. Stop timer and immediately update UI to 'processing'
    if (timerIntervalRef.current) clearInterval(timerIntervalRef.current)
    setElapsedTime(0)
    setRecordingState('processing')

    // 2. Use a timeout to allow the UI to re-render *before* we start the heavy encoding task.
    // This prevents the UI from freezing on the "Stop" button.
    setTimeout(() => {
      // 3. Perform the encoding and cleanup
      encodeToMp3()

      if (scriptProcessorRef.current) scriptProcessorRef.current.disconnect()
      if (streamRef.current) streamRef.current.getTracks().forEach(track => track.stop())
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') audioContextRef.current.close()

      // The `encodeToMp3` function will set the audioURL, which will hide the button and show the player.
      // We can reset the state here for good measure.
      setRecordingState('idle')
    }, 0);
  };

  const encodeToMp3 = () => {
    if (!audioContextRef.current || rawAudioChunksRef.current.length === 0) return
    const sampleRate = audioContextRef.current.sampleRate

    const totalLength = rawAudioChunksRef.current.reduce((acc, chunk) => acc + chunk.length, 0)
    const combinedSamples = new Float32Array(totalLength)
    let offset = 0
    for (const chunk of rawAudioChunksRef.current) {
      combinedSamples.set(chunk, offset)
      offset += chunk.length
    }

    const samplesI16 = new Int16Array(combinedSamples.length)
    for (let i = 0; i < combinedSamples.length; i++) {
      samplesI16[i] = combinedSamples[i] * 32767
    }

    const mp3encoder = new lamejs.Mp3Encoder(1, sampleRate, 128)
    const mp3Data = []
    for (let i = 0; i < samplesI16.length; i += 1152) {
      const sampleChunk = samplesI16.subarray(i, i + 1152)
      const mp3buf = mp3encoder.encodeBuffer(sampleChunk)
      if (mp3buf.length > 0) mp3Data.push(mp3buf)
    }
    const mp3buf = mp3encoder.flush();
    if (mp3buf.length > 0) mp3Data.push(mp3buf)

    const mp3Blob = new Blob(mp3Data, { type: 'audio/mp3' })
    const url = URL.createObjectURL(mp3Blob)

    setAudioBlob(mp3Blob)
    setAudioURL(url)
  };

  // --- The rest of your component logic remains the same ---
  // ...

  const onDeleteAudioClick = () => {
    setAudioBlob(null)
    setAudioURL(null)
  }

  const onReRecordAudioClick = async () => {
    await recordAudio()
  }

  const currentlyUploading = uploadQueue.find(file => createdGuestbookEntry && file.guestbookEntry && file.guestbookEntry.id === createdGuestbookEntry.id && !isStatusFinalState(file.status))
  const isUploaded = uploadQueue.find(file => createdGuestbookEntry && file.guestbookEntry && file.guestbookEntry.id === createdGuestbookEntry.id && isStatusFinalState(file.status))

  useEffect(() => {
    if (isUploaded) {
      refetch().then(() => {
        setCreatedGuestbookEntry(null)
        onCreateFinish()
      })
    }
  }, [isUploaded, onCreateFinish, setCreatedGuestbookEntry, refetch]);

  const onImageClick = () => inputFileRef.current!.click();
  const onFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files?.length) setImageFile(e.target.files[0]);
  };
  const onImageDeleteClick = () => setImageFile(null);

  const uploadData = async () => {
    const audioFile = audioBlob ? new File([audioBlob], 'audio.mp3', { type: 'audio/mp3' }) : null;
    const res = await createGuestbookEntry(album, content, audioFile);
    const guestbookEntry: AlbumGuestbookEntry = res.data;
    setCreatedGuestbookEntry(guestbookEntry);
    if (imageFile) registerFileUploads(album, [{ file: imageFile, guestbookEntry }]);
    else { await refetch(); onCreateFinish(); }
    return res;
  };

  const onPostClick = async () => {
    if (isPosting) return;
    await updateHelper(isPosting, setIsPosting, uploadData, 'Posting guestbook entry', 'Fehler beim Posten', async () => {});
  };

  const hasAudio = !!audioURL
  const hasImage = !!imageFile
  const hasAudioOrImage = hasAudio || hasImage
  const isPostable = !!content || hasAudio || hasImage

  return (
    <div className='flex flex-col gap-3 bg-neutral border-base-content border-2 border-opacity-10 shadow rounded-xl w-full p-4 items-center'>
      <h2 className='font-bold text-xl'>{t('common.newGuestbookEntry')}</h2>
      <Textarea maxLength={1000} placeholder={t('common.enterText')} className='border-base-content border-2 border-opacity-10 w-full bg-neutral' value={content} onChange={e => setContent(e.target.value)} />
      <div className={'flex w-full ' + (hasAudioOrImage ? 'flex-col items-center gap-5' : 'flex-row justify-evenly')}>
        {/* --- MODIFIED JSX with the new 'processing' state --- */}
        {!hasAudio && (
          <div className={'border-base-content border-2 border-opacity-10 shadow rounded-full h-[4rem] flex justify-center items-center ' + (recordingState !== 'idle' ? 'bg-primary w-[unset] px-3 text-neutral font-semibold cursor-default' : 'w-[4rem] cursor-pointer')} onClick={onAudioClick}>
            {recordingState === 'idle' && <IconMicrophone size={30} />}
            {recordingState === 'initiating' && <><Loading className='me-3' color='secondary' /> {t('common.startRecording')}...</>}
            {recordingState === 'recording' && <div className="w-full text-center cursor-pointer">{t('common.endRecording')} ({formatTime(elapsedTime)})</div>}
            {recordingState === 'processing' && <><Loading className='me-3' color='secondary' /> {t('common.processAudio')}...</>}
          </div>
        )}

        {hasAudio && (
          <div className='flex flex-col gap-3 w-full'>
            <audio className='w-full border-2 border-base-content border-opacity-5 rounded-full bg-secondary' controls src={audioURL!} />
            <div className='flex flex-row gap-3'>
              <Button size='sm' className='flex-1' color='primary' onClick={onReRecordAudioClick}>Neu aufnehmen</Button>
              <Button size='sm' className='flex-1' color='primary' variant='outline' onClick={onDeleteAudioClick}>{t('common.delete')}</Button>
            </div>
          </div>
        )}
        {!hasImage && <div className='border-base-content border-2 border-opacity-10 shadow rounded-full w-[4rem] h-[4rem] flex justify-center items-center cursor-pointer' onClick={onImageClick}>
          <IconCamera size={30} />
        </div>}
        {hasImage && (
          <div className='flex flex-col gap-3 w-full'>
            <img className='w-full rounded-xl shadow' src={URL.createObjectURL(imageFile!)} alt='' />
            <div className='flex flex-row gap-3'>
              <Button size='sm' className='flex-1' color='primary' variant='outline' onClick={onImageDeleteClick}>{t('common.delete')}</Button>
            </div>
          </div>
        )}
        <div className='hidden'>
          <FileInput ref={inputFileRef} accept='image/*' onInput={onFileSelect} />
        </div>
      </div>
      <Button loading={isPosting || !!currentlyUploading} disabled={!isPostable} className='px-[3rem]' color='primary' onClick={onPostClick}>{t('common.save')}</Button>
    </div>
  );
}

function AlbumGuestbookEntryComponent({ entry, album, onDelete }: { entry: AlbumGuestbookEntry, album: Album, onDelete: (guestbookEntry: AlbumGuestbookEntry) => void }) {
  const { profile } = useProfile()
  const date = new Date(entry.created_at)
  const canDelete = profile && (profile.username == album.author || profile.username == entry.author) && !entry.is_demo
  const [isDeleting, setIsDeleting] = useState(false)
  const deleteModalRef = useRef<HTMLDialogElement>(null);
  const { deleteAlbumGuestbookEntry } = useClient()
  const { t } = useTranslation()

  function onDeleteClick() {
    deleteModalRef.current!.showModal()
  }

  async function onDeleteConfirm() {
    await updateHelper(
      isDeleting,
      setIsDeleting,
      deleteAlbumGuestbookEntry,
      'Deleting guestbook entry',
      'Fehler beim Löschen',
      async () => {
        onDelete(entry)
      },
      entry,
    )
  }

  const imageComponent = entry.files && entry.files.length > 0 ? <div>
    <img className='rounded-xl w-full max-w-full overflow-hidden object-contain' src={entry.files[0].detail || entry.files[0].file} alt="" />
  </div> : null
  return <div className='flex flex-col gap-2 items-center'>
    <div className='text-2xl font-bold mt-5'>{ entry.author_display_name }</div>
    <div className='flex flex-col gap-3 bg-primary bg-opacity-15 border-base-content border-2 border-opacity-10 shadow rounded-xl w-full p-4'>
      { entry.audio && <div className='flex flex-col w-full justify-center items-center'>
        <audio className='w-full border-2 border-base-content border-opacity-5 rounded-full bg-secondary' controls>
          <source src={entry.audio} />
        </audio>
      </div> }
      { entry.content && <p className='text-[1.1rem] whitespace-pre-wrap text-center'>{entry.content}</p> }
      {imageComponent}
      <div className='relative'>
        <p className='opacity-50 italic text-sm text-center'>{date.toLocaleDateString()}, {date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</p>
        { canDelete && <Button className='absolute right-0 top-[50%]' color='ghost' size='sm' style={{transform: 'translateY(-50%)'}} onClick={onDeleteClick}><IconTrash size='20' /></Button> }
      </div>
      <Modal ref={deleteModalRef}>
        <Modal.Header>
          <div className='flex flex-row gap-2 justify-center'>
            <div className='bg-primary text-neutral rounded-full p-1'>
              <IconTrash size='20'/>
            </div>
            {t('common.delete')}
          </div>
          <form method="dialog">
            <button disabled={isDeleting} className="btn btn-sm btn-circle btn-ghost absolute right-5 top-5">
              <IconX />
            </button>
          </form>
        </Modal.Header>
        <Modal.Body>
          <div className='flex flex-col gap-2 items-center'>
            <Button disabled={isDeleting} loading={isDeleting} className='px-5' color='error' onClick={onDeleteConfirm}>
              {t('common.delete')}
            </Button>
            <form method="dialog">
              <Button disabled={isDeleting} color='ghost'>
                {t('common.cancel')}
              </Button>
            </form>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  </div>
}
